import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Flag } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { createReport } from '../../lib/api-client';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  postId?: number;
  communityPostId?: string;
  commentId?: string;
}

const REASON_OPTIONS = [
  'Spam',
  'Harassment',
  'Misinformation',
  'Inappropriate content',
  'Other',
];

export function ReportModal({ isOpen, onClose, postId, communityPostId, commentId }: ReportModalProps) {
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const reason = selectedReason === 'Other' ? customReason.trim() : selectedReason;

  const handleSubmit = async () => {
    if (!reason) return;
    setSubmitting(true);
    setError('');
    try {
      await createReport({
        ...(postId ? { postId } : {}),
        ...(communityPostId ? { communityPostId } : {}),
        ...(commentId ? { commentId } : {}),
        reason,
      });
      setSubmitted(true);
      setTimeout(() => {
        onClose();
        // Reset state after close animation
        setTimeout(() => {
          setSelectedReason('');
          setCustomReason('');
          setSubmitted(false);
        }, 200);
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setSelectedReason('');
      setCustomReason('');
      setSubmitted(false);
      setError('');
    }, 200);
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-center justify-center px-4"
          style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className="w-full max-w-md rounded-2xl overflow-hidden"
            style={{
              background: 'var(--navbar-bg, #1a1a2e)',
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-5 py-4"
              style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
            >
              <div className="flex items-center gap-2">
                <Flag size={18} style={{ color: '#ef4444' }} />
                <h3 className="text-base font-bold" style={{ color: 'var(--text-color, #fff)' }}>
                  Report Content
                </h3>
              </div>
              <button
                onClick={handleClose}
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}
              >
                <X size={18} style={{ color: 'var(--text-color, #fff)' }} />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-4">
              {submitted ? (
                <div className="flex flex-col items-center gap-3 py-6">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(34,197,94,0.15)' }}
                  >
                    <Flag size={20} style={{ color: '#22c55e' }} />
                  </div>
                  <p className="text-sm font-medium" style={{ color: 'var(--text-color, #fff)' }}>
                    Report submitted. Thank you.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-sm mb-4" style={{ color: 'var(--text-color, #fff)', opacity: 0.6 }}>
                    Why are you reporting this content?
                  </p>

                  {/* Reason options */}
                  <div className="flex flex-col gap-2 mb-4">
                    {REASON_OPTIONS.map((opt) => (
                      <button
                        key={opt}
                        onClick={() => setSelectedReason(opt)}
                        className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-left transition-colors"
                        style={{
                          backgroundColor: selectedReason === opt
                            ? 'var(--color-accent, #06b6d4)'
                            : 'rgba(255,255,255,0.06)',
                          color: selectedReason === opt ? '#fff' : 'var(--text-color, #fff)',
                          border: `1px solid ${selectedReason === opt ? 'var(--color-accent, #06b6d4)' : 'rgba(255,255,255,0.08)'}`,
                        }}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>

                  {/* Custom reason input */}
                  {selectedReason === 'Other' && (
                    <textarea
                      value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)}
                      placeholder="Describe the issue..."
                      className="w-full rounded-xl p-3 resize-none outline-none mb-4"
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: 'var(--text-color, #fff)',
                        minHeight: 80,
                        fontSize: 16,
                      }}
                      maxLength={500}
                    />
                  )}

                  {error && (
                    <p className="text-xs mb-3" style={{ color: '#ef4444' }}>{error}</p>
                  )}

                  {/* Submit button */}
                  <button
                    onClick={handleSubmit}
                    disabled={!reason || submitting}
                    className="w-full py-3 rounded-xl font-semibold text-sm disabled:opacity-50"
                    style={{ backgroundColor: '#ef4444', color: '#fff' }}
                  >
                    {submitting ? 'Submitting...' : 'Submit Report'}
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
